/**
 * Grundriss-Flow: Session-Persistenz im Browser (sessionStorage).
 *
 * Analog zu {@link ./wizardSession} für den Staging-Assistenten: Der Grundriss-
 * Flow (floorplan-upload → … → floorplan-branding) hält seine UI-Position im
 * flüchtigen React-State (`currentView`). Beim Weg-Navigieren (Sidebar, Overlay-
 * Builder, Browser-Back) ginge diese Position verloren.
 *
 * Hier persistieren wir NUR die resumable UI-Position (welcher Schritt) pro
 * Projekt. Die eigentlichen Grundriss-Daten (2D/3D-Generierungen, Transforms,
 * Config) leben in der DB und werden beim Wiedereintritt via
 * `loadExistingFloorplan` daraus rekonstruiert — die Session sagt nur "welcher
 * Schritt". Ohne Session startet der Flow eben beim Upload (wo der
 * "Weiterarbeiten"-Button aus dem DB-Stand fortsetzen kann).
 *
 * sessionStorage (nicht localStorage): pro Tab, wird beim Schließen aufgeräumt.
 * Robustheit + Verfall + sichere Zugriffe: identisch zu wizardSession.
 */

export type FloorplanFlowView =
  | 'floorplan-upload'
  | 'floorplan-unit-selection'
  | 'floorplan-labels'
  | 'floorplan-editor'
  | 'floorplan-review'
  | 'floorplan-3d'
  | 'floorplan-branding';

/** Schritt-Reihenfolge (für Anzeige/Clamping). */
export const FLOORPLAN_FLOW_VIEWS: readonly FloorplanFlowView[] = [
  'floorplan-upload',
  'floorplan-unit-selection',
  'floorplan-labels',
  'floorplan-editor',
  'floorplan-review',
  'floorplan-3d',
  'floorplan-branding',
];

/** Kurzlabel je Schritt — für die "Fortsetzen"-Bubble. */
export const FLOORPLAN_STEP_LABEL: Record<FloorplanFlowView, string> = {
  'floorplan-upload': 'Hochladen',
  'floorplan-unit-selection': 'Einheit wählen',
  'floorplan-labels': 'Beschriften',
  'floorplan-editor': 'Bearbeiten',
  'floorplan-review': 'Prüfen',
  'floorplan-3d': '3D-Ansicht',
  'floorplan-branding': 'Branding',
};

export interface FloorplanSessionSnapshot {
  projectId: string;
  view: FloorplanFlowView;
  updatedAt: number;
}

const KEY_PREFIX = 'estage:floorplan:';
/** Persistenz-Schema. Bei inkompatiblen Änderungen erhöhen → Alt-Sessions verfallen sauber. */
const SCHEMA_VERSION = 1;
/** Sicherheits-Verfall: veraltete Sessions nach 24 h ignorieren/aufräumen. */
const MAX_AGE_MS = 1000 * 60 * 60 * 24;

const storageKey = (projectId: string): string => `${KEY_PREFIX}${projectId}`;

/**
 * Sicherer Zugriff auf sessionStorage. In manchen Privacy-Modi wirft schon der
 * Property-Zugriff — daher gekapselt. Gibt null zurück, wenn nicht verfügbar.
 */
function getStore(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function normalizeView(v: unknown): FloorplanFlowView {
  // Ungültiger/unbekannter Schritt ist unkritisch: auf den Start zurückfallen,
  // statt die ganze Session zu verwerfen (Upload bietet DB-"Weiterarbeiten").
  return typeof v === 'string' && (FLOORPLAN_FLOW_VIEWS as readonly string[]).includes(v)
    ? (v as FloorplanFlowView)
    : 'floorplan-upload';
}

/**
 * Validiert + normalisiert eine rohe (geparste) Session gegen die erwartete
 * projectId. Gibt null zurück, wenn kritisch ungültig oder abgelaufen.
 */
function sanitizeSnapshot(data: unknown, expectedProjectId: string): FloorplanSessionSnapshot | null {
  if (!isRecord(data)) return null;

  // Inkompatible Schema-Version → verwerfen.
  if (typeof data.version === 'number' && data.version !== SCHEMA_VERSION) return null;

  // Projekt muss exakt passen (Schutz vor Key/Value-Mismatch).
  if (typeof data.projectId !== 'string' || data.projectId !== expectedProjectId) return null;

  // updatedAt: endliche Zahl Pflicht; zu alt → verwerfen.
  const updatedAt = data.updatedAt;
  if (typeof updatedAt !== 'number' || !Number.isFinite(updatedAt)) return null;
  if (Date.now() - updatedAt > MAX_AGE_MS) return null;

  return {
    projectId: expectedProjectId,
    view: normalizeView(data.view),
    updatedAt,
  };
}

/** Entfernt abgelaufene/kaputte Grundriss-Einträge (opportunistisch, z. B. bei Quota). */
function pruneFloorplanSessions(keepProjectId?: string): void {
  const store = getStore();
  if (!store) return;
  const keys: string[] = [];
  try {
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && k.startsWith(KEY_PREFIX)) keys.push(k);
    }
  } catch {
    return;
  }
  const now = Date.now();
  for (const key of keys) {
    if (keepProjectId && key === storageKey(keepProjectId)) continue;
    let stale = true;
    try {
      const raw = store.getItem(key);
      if (raw) {
        const d = JSON.parse(raw) as { updatedAt?: unknown; version?: unknown };
        stale =
          (typeof d?.version === 'number' && d.version !== SCHEMA_VERSION) ||
          !(typeof d?.updatedAt === 'number' && Number.isFinite(d.updatedAt) && now - d.updatedAt <= MAX_AGE_MS);
      }
    } catch {
      stale = true;
    }
    if (stale) {
      try {
        store.removeItem(key);
      } catch {
        /* ignore */
      }
    }
  }
}

export function saveFloorplanSession(snapshot: Omit<FloorplanSessionSnapshot, 'updatedAt'>): void {
  if (!snapshot?.projectId || typeof snapshot.projectId !== 'string') return;
  const store = getStore();
  if (!store) return;
  const payload = JSON.stringify({
    projectId: snapshot.projectId,
    view: normalizeView(snapshot.view),
    version: SCHEMA_VERSION,
    updatedAt: Date.now(),
  });
  const key = storageKey(snapshot.projectId);
  try {
    store.setItem(key, payload);
  } catch {
    try {
      pruneFloorplanSessions(snapshot.projectId);
      store.setItem(key, payload);
    } catch {
      // Endgültig nicht speicherbar — unkritisch, die DB ist der Rückfall.
    }
  }
}

export function loadFloorplanSession(projectId: string): FloorplanSessionSnapshot | null {
  if (!projectId || typeof projectId !== 'string') return null;
  const store = getStore();
  if (!store) return null;

  let raw: string | null;
  try {
    raw = store.getItem(storageKey(projectId));
  } catch {
    return null;
  }
  if (!raw) return null;

  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    clearFloorplanSession(projectId);
    return null;
  }

  const snapshot = sanitizeSnapshot(data, projectId);
  if (!snapshot) {
    clearFloorplanSession(projectId);
    return null;
  }
  return snapshot;
}

export function clearFloorplanSession(projectId: string): void {
  if (!projectId) return;
  const store = getStore();
  if (!store) return;
  try {
    store.removeItem(storageKey(projectId));
  } catch {
    // ignore
  }
}

/** True, wenn für dieses Projekt ein (nicht abgelaufener) Grundriss-Fortschritt vorliegt. */
export function hasFloorplanSession(projectId: string): boolean {
  return loadFloorplanSession(projectId) !== null;
}

/**
 * Jüngste (zuletzt aktualisierte) offene Grundriss-Session über alle Projekte —
 * für die "Grundriss fortsetzen"-Bubble. Abgelaufene/kaputte Einträge werden
 * ignoriert (und von loadFloorplanSession aufgeräumt).
 */
export function getLatestFloorplanSession(): FloorplanSessionSnapshot | null {
  const store = getStore();
  if (!store) return null;

  const projectIds: string[] = [];
  try {
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key && key.startsWith(KEY_PREFIX)) projectIds.push(key.slice(KEY_PREFIX.length));
    }
  } catch {
    return null;
  }

  let latest: FloorplanSessionSnapshot | null = null;
  for (const projectId of projectIds) {
    const session = loadFloorplanSession(projectId);
    if (session && (!latest || session.updatedAt > latest.updatedAt)) {
      latest = session;
    }
  }
  return latest;
}
